// What the player tapped. View-side by necessity (00-architecture.md §1): it
// needs the camera, the canvas and the bugs' world transforms — the sim only
// ever sees the `Command` that comes out of it.
//
// The ray is cast from the camera through a screen point into *cube-local*
// space, where both tests live: a sphere per living bug, and the grid march
// (Amanatides & Woo DDA) that finds the first solid block.
//
// Ports the picking half of `Cube.MouseInteractionWithObjects`, which raycasts
// the whole scene, sorts the hits by distance and takes the first `bug*`,
// `powerup*` or `hit{xyz}` collider. Apples are deliberately not pickable —
// the original only ever names those three.

import type { Mat4 } from "../math";
import type { BlockState, Cell } from "../game/blocks";
import { frameCube } from "../camera";
import { bugFrame, isKillable, type Bug, type BugId } from "../game/bugs";
import { isCollectable, powerUpFrame, type PowerUp, type PowerUpId } from "../game/powerups";
import { POWERUP_DEFS } from "../game/defs";
import { MODEL_CATALOG } from "./model-catalog";
import { MODEL_SCALE } from "./bug-renderer";

type Vec3 = [number, number, number];

/**
 * Tap radius around a bug's body centre, world units. The bugs are ~0.1 world
 * long, so this is a little over their visual half-length: generous on purpose,
 * matching the original's forgiving box colliders and giving a thumb on a phone
 * something it can actually hit.
 */
export const BUG_PICK_RADIUS = 0.12;

/** All picking needs of a grid: its extent and the state of each cell. */
export interface PickableGrid {
  readonly size: number;
  state(x: number, y: number, z: number): BlockState;
}

/** The sim state a tap is resolved against. */
export interface PickableWorld {
  readonly blocks: PickableGrid;
  readonly bugs: ReadonlyMap<BugId, Bug>;
  readonly powerups: ReadonlyMap<PowerUpId, PowerUp>;
}

export type PickResult =
  | { readonly kind: "bug"; readonly id: BugId }
  | { readonly kind: "powerup"; readonly id: PowerUpId }
  /** The nearest damaged cell along the ray — always a valid repair target. */
  | { readonly kind: "block"; readonly cell: Cell }
  | null;

interface Ray {
  readonly origin: Vec3;
  /** Unit length, so `t` is a distance and the two tests compare directly. */
  readonly dir: Vec3;
}

// Apply the transpose of the model's rotation part; for a pure rotation
// matrix that is its inverse, taking the ray from world into object space.
function rotateByTranspose(m: Mat4, v: Vec3): Vec3 {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[4] * v[0] + m[5] * v[1] + m[6] * v[2],
    m[8] * v[0] + m[9] * v[1] + m[10] * v[2],
  ];
}

/** Camera ray through a client-space point, in cube-local coordinates. */
export function screenRay(
  model: Mat4,
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number,
): Ray {
  const rect = canvas.getBoundingClientRect();
  const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
  const ndcY = 1 - ((clientY - rect.top) / rect.height) * 2;
  const cam = frameCube(rect.width / rect.height);
  const f = 1 / Math.tan(cam.fovY / 2);

  // View is a pure translation along z, so the world-space ray is simple.
  const origin = rotateByTranspose(model, [0, 0, cam.dist]);
  const raw = rotateByTranspose(model, [(ndcX * cam.aspect) / f, ndcY / f, -1]);
  const len = Math.hypot(raw[0], raw[1], raw[2]);
  return { origin, dir: [raw[0] / len, raw[1] / len, raw[2] / len] };
}

/**
 * Distance along the ray to a sphere of `BUG_PICK_RADIUS` around a lifted
 * surface frame — the frame's position raised along its normal by the same
 * amount the renderer lifts the model, so the sphere sits on the body rather
 * than under its feet. Null on a miss (or with the camera inside/past it).
 */
function sphereHit(
  frame: { pos: readonly [number, number, number]; normal: readonly [number, number, number] },
  lift: number,
  ray: Ray,
): number | null {
  const cx = frame.pos[0] + frame.normal[0] * lift - ray.origin[0];
  const cy = frame.pos[1] + frame.normal[1] * lift - ray.origin[1];
  const cz = frame.pos[2] + frame.normal[2] * lift - ray.origin[2];
  // `dir` is unit length, so the quadratic reduces to this projection form.
  const along = cx * ray.dir[0] + cy * ray.dir[1] + cz * ray.dir[2];
  const gap2 = cx * cx + cy * cy + cz * cz - along * along;
  const r2 = BUG_PICK_RADIUS * BUG_PICK_RADIUS;
  if (gap2 > r2) return null;
  const t = along - Math.sqrt(r2 - gap2);
  return t < 0 ? null : t;
}

type SphereTarget =
  | { readonly kind: "bug"; readonly id: BugId }
  | { readonly kind: "powerup"; readonly id: PowerUpId };

/**
 * Nearest living bug or collectable power-up the ray passes through — one
 * distance sort over both, as the original's raycast resolves everything by
 * distance and breaks on the first hit. Bugs that are already dying or being
 * carried off are skipped, as are taken/killed power-ups: a tap on one must
 * fall through to the block behind it rather than be swallowed.
 */
function nearestTarget(world: PickableWorld, ray: Ray): { target: SphereTarget; t: number } | null {
  let best: { target: SphereTarget; t: number } | null = null;
  for (const bug of world.bugs.values()) {
    if (!isKillable(bug.state)) continue;
    const t = sphereHit(bugFrame(bug), MODEL_CATALOG[bug.kindDef].lift * MODEL_SCALE, ray);
    if (t !== null && (best === null || t < best.t)) {
      best = { target: { kind: "bug", id: bug.id }, t };
    }
  }
  for (const powerUp of world.powerups.values()) {
    if (!isCollectable(powerUp.state)) continue;
    const name = POWERUP_DEFS[powerUp.slot].model;
    const t = sphereHit(powerUpFrame(powerUp), MODEL_CATALOG[name].lift * MODEL_SCALE, ray);
    if (t !== null && (best === null || t < best.t)) {
      best = { target: { kind: "powerup", id: powerUp.id }, t };
    }
  }
  return best;
}

/** Where the ray enters the cube's bounding box, or null if it misses. */
function enterCube(ray: Ray): number | null {
  let tMin = -Infinity;
  let tMax = Infinity;
  for (let a = 0; a < 3; a++) {
    if (Math.abs(ray.dir[a]) < 1e-9) {
      if (ray.origin[a] < -0.5 || ray.origin[a] > 0.5) return null;
      continue;
    }
    const t1 = (-0.5 - ray.origin[a]) / ray.dir[a];
    const t2 = (0.5 - ray.origin[a]) / ray.dir[a];
    tMin = Math.max(tMin, Math.min(t1, t2));
    tMax = Math.min(tMax, Math.max(t1, t2));
  }
  if (tMax < Math.max(tMin, 0)) return null;
  return Math.max(tMin, 0);
}

interface BlockHit {
  readonly cell: Cell;
  /** Distance along the ray at which the cell is entered. */
  readonly t: number;
}

/**
 * The nearest damaged cell along the ray — anything that is not `healthy`.
 *
 * Ports `Cube.MouseInteractionWithObjects`, which walks its distance-sorted
 * raycast hits collecting non-healthy blocks and **breaks on the first Healthy
 * one**: you cannot repair through intact grass, but you can tap the visible
 * back wall of a hole and repair the hole itself. Returning the damaged cell
 * directly replaces the `prev` bookkeeping an earlier version needed, because a
 * ghost is now a cell in its own right rather than an absence.
 *
 * Not ported: the original collects *every* damaged cell along the ray and
 * tries `RepairBlock` on each until one succeeds, so a nearest ghost whose
 * repair is refused (a walker on it) falls through to the next one behind it.
 * Returning only the nearest keeps picking a pure view-side query; the fallback
 * matters solely when a walker is standing on the target.
 */
function marchBlocks(grid: PickableGrid, ray: Ray): BlockHit | null {
  const entry = enterCube(ray);
  if (entry === null) return null;

  const n = grid.size;
  const cellSize = 1 / n;
  const tEnter = entry + 1e-6;
  const cell: Vec3 = [0, 0, 0];
  const step: Vec3 = [0, 0, 0];
  const tNext: Vec3 = [Infinity, Infinity, Infinity];
  const tDelta: Vec3 = [Infinity, Infinity, Infinity];
  for (let a = 0; a < 3; a++) {
    const p = ray.origin[a] + ray.dir[a] * tEnter;
    cell[a] = Math.min(n - 1, Math.max(0, Math.floor((p + 0.5) * n)));
    if (ray.dir[a] > 0) step[a] = 1;
    else if (ray.dir[a] < 0) step[a] = -1;
    if (step[a] !== 0) {
      const boundary = (cell[a] + (step[a] > 0 ? 1 : 0)) * cellSize - 0.5;
      tNext[a] = (boundary - ray.origin[a]) / ray.dir[a];
      tDelta[a] = cellSize / Math.abs(ray.dir[a]);
    }
  }

  // In practice this decides on the very first cell the ray enters — every cell
  // is either healthy (stop) or damaged (target). The walk is kept because the
  // entry point is nudged by an epsilon and may land a hair outside the grid.
  let tCell = tEnter;
  for (let i = 0; i < 3 * n + 3; i++) {
    if (cell.every((c) => c >= 0 && c < n)) {
      if (grid.state(cell[0], cell[1], cell[2]) === "healthy") return null;
      return { cell: [cell[0], cell[1], cell[2]], t: tCell };
    }
    const a = tNext[0] < tNext[1] ? (tNext[0] < tNext[2] ? 0 : 2) : tNext[1] < tNext[2] ? 1 : 2;
    tCell = tNext[a];
    cell[a] += step[a];
    tNext[a] += tDelta[a];
  }
  return null;
}

/**
 * What a tap at a client-space point hits. Bugs and power-ups win ties over
 * blocks: they stand *on* the surface, so a tap aimed at one always also lands
 * on the block beneath it, and the player meant the thing standing there.
 */
export function pick(
  clientX: number,
  clientY: number,
  canvas: HTMLCanvasElement,
  cubeModel: Mat4,
  world: PickableWorld,
): PickResult {
  const ray = screenRay(cubeModel, canvas, clientX, clientY);
  const hit = nearestTarget(world, ray);
  const block = marchBlocks(world.blocks, ray);
  if (hit !== null && (block === null || hit.t <= block.t)) return hit.target;
  if (block === null) return null;
  return { kind: "block", cell: block.cell };
}
